import React, { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { LogOut, Users } from 'lucide-react'
import { useKeycloak } from '@react-keycloak/web'
import { jwtDecode } from "jwt-decode";
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';

import { UPDATE_PARCEL } from 'src/graphql/mutations/parcel.mutations';
import { PARCEL_QUERY } from 'src/graphql/queries/parcel.queries'
import { AppAuthProvider } from 'src/middleware'
import { relativeTime } from 'src/utils/time';
import { useParcelStore } from 'src/store/parcel';
import { removeDefaultRole } from 'src/utils/common';
import {
  connect as connectTodoSync,
  disconnect as disconnectTodoSync,
  subscribeConnectionStatus,
  getAllClients,
  wsProvider,
  logStoreData,
  cleanupDuplicates,
} from 'src/store/todo';

import Modal from '../shared/modal';
import Button from '../shared/button';

const Settings: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected'>('disconnected')
  const [isOnline, setIsOnline] = useState(true)
  const [open, setOpen] = useState(false)
  const [parcelData, setParcelData] = useState<Record<string, any>>({})
  const [clients, setClients] = useState<any[]>([])
  const [cleanupOpen, setCleanupOpen] = useState(false)
  const [cleanupReport, setCleanupReport] = useState<any>(null)
  const [isCleaning, setIsCleaning] = useState(false)
  const [isLoggingEnabled, setIsLoggingEnabled] = useState(() => {
    return localStorage.getItem('debug_logging_enabled') === 'true'
  })
  const { authProvider } = AppAuthProvider()
  const { keycloak } = useKeycloak();
  const { token } = keycloak
  const setParcelState = useParcelStore((state) => state.setParcelState)
  const setRoomData = useParcelStore(state => state.setRoomData)
  const room = useParcelStore((state) => state.roomData)
  const activeParcel = useParcelStore((state) => state.parcel)
  const setActiveParcel = useParcelStore((state) => state.setParcelState)

  const decoded: UserSessionJWT | null = token ? jwtDecode(token) : null;

  const { data, loading } = useQuery(PARCEL_QUERY,{
    fetchPolicy: 'network-only',
  })

  const [updateParcelVersion, { loading: updating }] = useMutation(UPDATE_PARCEL,{
    onCompleted: async () => {
       setActiveParcel('')
       await disconnectTodoSync(true)
       setRoomData(null)
    },
    onError: (error) => {
      console.error('Error updating parcel version:', error)
    },
    refetchQueries: ['ParcelTask'],
  })

  const intials = decoded?.name?.split(' ')

  const switchParcel = () => {
    setParcelState(parcelData?.id)

    setRoomData({
      roomId: parcelData?.rooms?.[0]?.room_id || '',
      ...parcelData
    })
    setOpen(false)
  }


  // Only log if logging is enabled
  useEffect(() => {
    if (isLoggingEnabled) {
      logStoreData()
    }
  }, [isLoggingEnabled, connectionStatus])

  const onParcelChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedParcel = data?.parcels?.find((p: Record<string, string>) => p.id === e.target.value)
    setParcelData(selectedParcel)
    setOpen(true)
    await disconnectTodoSync(true)
  }

  useEffect(() => {
    const unsubscribe = subscribeConnectionStatus(setConnectionStatus)
    return unsubscribe
  }, [])

  useEffect(() => {
    if (!wsProvider?.awareness) return

    const updateClients = () => {
      setClients(getAllClients())
    }

    updateClients()
    wsProvider.awareness.on('change', updateClients)

    return () => {
      if (wsProvider?.awareness) {
        wsProvider.awareness.off('change', updateClients)
      }
    }
  }, [connectionStatus])

  const onParcelVersionUpdate = () => {
    if(!activeParcel || !room?.rooms?.[0]?.version) {
      toast.error("Please select a parcel first")
      return
    }
    updateParcelVersion({
      variables: {
        id: room?.rooms?.[0]?.id,
        version: `${(Number(room?.rooms?.[0]?.version) + 1)}` || ''
      }
    })
  }

  // Handle online/offline toggle
  const handleToggleOnline = () => {
    if (isOnline) {
      disconnectTodoSync()
      setIsOnline(false)
    } else {
      connectTodoSync({ room: room?.roomId, user: decoded as UserSessionJWT || null })
      setIsOnline(true)
    }
  }

  // Format last seen time
  const formatLastSeen = (heartbeat: number): string => {
    if (!heartbeat) return 'Never'
    const now = Date.now()
    const diff = now - heartbeat
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)

    if (seconds < 30) return 'Just now'
    if (seconds < 60) return `${seconds}s ago`
    if (minutes < 60) return `${minutes}m ago`
    return 'Offline'
  }

  // Handle cleanup duplicates
  const handleCleanupDuplicates = () => {
    setIsCleaning(true)
    try {
      const result = cleanupDuplicates()
      setCleanupReport(result)
      setCleanupOpen(true)

      if (result.success && result.report.total > 0) {
        toast.success(`Cleaned ${result.report.total} duplicate entries!`)
      } else if (result.success && result.report.total === 0) {
        toast.success('No duplicates found!')
      } else {
        toast.error(result.error || 'Failed to clean duplicates')
      }
    } catch (error) {
      console.error('Error cleaning duplicates:', error)
      toast.error('Failed to clean duplicates')
    } finally {
      setIsCleaning(false)
    }
  }

  // Handle toggle logging
  const handleToggleLogging = () => {
    const newValue = !isLoggingEnabled
    setIsLoggingEnabled(newValue)
    localStorage.setItem('debug_logging_enabled', String(newValue))
    if (newValue) {
      toast.success('Debug logging enabled')
      logStoreData()
    } else {
      toast.success('Debug logging disabled')
    }
  }

  return (
    <div className='pb-44'>
      <div className='flex flex-col gap-2 items-center justify-center py-7 border-b border-neutral-200'>
        <div className='w-14 h-14 p-1 rounded-full flex items-center justify-center bg-green-700 text-white'>
          <p className='text-2xl uppercase'>{`${intials?.[0]?.charAt(0)}${intials?.[1].charAt(0)}`}</p>
        </div>
        <div className='text-center'>
          <p className='text-xl font-medium capitalize'> {decoded?.name} </p>
          <p>{decoded?.email} </p>
        </div>
      </div>

      {/* SyncedStore Connection Status */}
      <div className='px-3 pt-3 mb-3'>
        <div className='border border-neutral-200 rounded-md p-3 bg-white'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <p className='font-semibold text-sm'>Sync Service</p>
              <div className='flex items-center gap-1'>
                <div
                  className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'}`}
                  style={{ boxShadow: connectionStatus === 'connected' ? '0 0 4px rgba(34, 197, 94, 0.5)' : '0 0 4px rgba(239, 68, 68, 0.5)' }}
                />
                <span className='text-xs text-gray-600'>
                  {connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
                </span>
              </div>
            </div>
            {/* Toggle Switch */}
            <label className='relative inline-flex items-center cursor-pointer'>
              <input
                type='checkbox'
                checked={isOnline}
                onChange={handleToggleOnline}
                className='sr-only peer'
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Connected Clients */}
      {connectionStatus === 'connected' && clients.length > 0 && (
        <div className='px-3 mb-3'>
          <div className='border border-neutral-200 rounded-md p-3 bg-white'>
            <div className='flex items-center gap-2 mb-3'>
              <Users className='w-4 h-4' />
              <p className='font-semibold text-sm'>Connected Clients ({clients.length})</p>
            </div>
            <div className='space-y-2 max-h-60 overflow-y-auto'>
              {clients.map((client) => (
                <div
                  key={client.id}
                  className={`p-2 rounded border ${client.isLocal ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                >
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <span className='text-lg'>{client.emoji}</span>
                      <div>
                        <p className='text-sm font-medium capitalize'>
                          {client.fruit} {client.isLocal && '(You)'}
                        </p>
                        <p className='text-xs text-gray-600'>
                          ID: {client.id} • {client.ip}
                        </p>
                      </div>
                    </div>
                    <div className='text-right'>
                      <div className='flex items-center gap-1'>
                        <div
                          className={`w-1.5 h-1.5 rounded-full ${client.heartbeat && Date.now() - client.heartbeat < 30000
                              ? 'bg-green-500'
                              : 'bg-gray-400'
                            }`}
                        />
                        <span className='text-xs text-gray-500'>
                          {formatLastSeen(client.heartbeat)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className='px-3 flex flex-col gap-2'>
        <div>
          <p className='font-semibold text-sm py-1'>Parcel</p>
          <select value={activeParcel} onChange={onParcelChange} className='text-sm border border-neutral-100 w-full focus:outline-none focus:ring-0 focus:border-neutral-100 px-1 h-11 rounded-md'>
            <option value="">{loading ? 'loading...' : 'Select Parcel'}</option>
            {data?.parcels?.map((parcel: Record<string, string>) => (
              <option key={parcel.id} value={parcel.id}>{parcel?.parcel_name}</option>))}
          </select>
        </div>
        <div>
          <p className='font-semibold text-sm'>Username</p>
          <div className='border border-neutral-100 text-neutral-600  p-2 h-10 capitalize rounded-md bg-white'>
            {decoded?.preferred_username}
          </div>
        </div>
        <div>
          <p className='font-semibold text-sm'>Email Verified</p>
          <div className={`border border-neutral-100 p-2 h-10 ${decoded?.email_verified ? 'text-green-700' : 'text-red-700'} rounded-md bg-white`}>
            {decoded?.email_verified ? 'Verified' : 'Unverified'}
          </div>
        </div>
        <div>
          <p className='font-semibold text-sm'>Role</p>
          <ul className='border space-x-3 border-neutral-100 p-2 m-h-10 flex gap-2 flex-wrap rounded-md bg-white'>
            {removeDefaultRole(decoded?.realm_access?.roles || [])?.map((role: string, idx: number) => (
              <li className='text-neutral-600' key={idx}>{role}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className='font-semibold text-sm'>Permisson</p>
          <ul className='border space-x-2 border-neutral-100 p-2 m-h-10 flex gap-2 flex-wrap rounded-md bg-white'>
            {decoded?.resource_access?.account?.roles?.map((role, idx) => (
              <li key={idx} className='text-neutral-600'>{role} </li>
            ))}
          </ul>
        </div>
        <div>
          <p className='font-semibold text-sm'>Room</p>
          <div className='border border-neutral-100 p-2 text-neutral-600 m-h-10 rounded-md bg-white'>
            {room?.roomId || 'N/A'}
          </div>
        </div>
        <div>
          <p className='font-semibold text-sm'>Last LoggedIn</p>
          <div className='border border-neutral-100 p-2 text-neutral-600 h-10 rounded-md bg-white'>
            {decoded?.auth_time ? relativeTime(decoded?.auth_time) : ''}
          </div>
        </div>
        <div>
          <p className='font-semibold text-sm'>Cached Version</p>
          <div className='border border-neutral-100 p-2 text-neutral-600 h-10 rounded-md bg-white'>
            Version: {import.meta.env.VITE_APP_VERSION || '1.0.0'}
          </div>
        </div>
        <div className='flex gap-2 mt-5'>
          <Button type="button" className='h-8 rounded-md flex gap-1 justify-center items-center text-sm w-24 bg-green-700 text-white hover:bg-green-800'
            onClick={onParcelVersionUpdate}
            disabled={updating}
            >
            <Icon icon='mdi:plus' width={20}/> {updating? "Updating...":"Version"}
          </Button>
          <Button type="button" className='h-8 rounded-md flex gap-1 justify-center items-center text-sm flex-1 bg-blue-600 text-white hover:bg-blue-700'
            onClick={handleCleanupDuplicates}
            disabled={isCleaning || connectionStatus === 'disconnected'}
            title={connectionStatus === 'disconnected' ? 'Connect to sync service first' : 'Check and clean duplicate data'}
            >
            <Icon icon='mdi:database-check' width={20}/> {isCleaning ? "Cleaning..." : "Check/Clean"}
          </Button>
        </div>

        {/* Debug Logging Toggle */}
        <div className='border border-neutral-200 rounded-md p-3 bg-white mt-3'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Icon icon='mdi:bug' width={20} className='text-orange-600'/>
              <div>
                <p className='font-semibold text-sm'>Debug Logging</p>
                <p className='text-xs text-gray-500'>Expensive operation - only enable when debugging</p>
              </div>
            </div>
            {/* Toggle Switch */}
            <label className='relative inline-flex items-center cursor-pointer'>
              <input
                type='checkbox'
                checked={isLoggingEnabled}
                onChange={handleToggleLogging}
                className='sr-only peer'
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
        </div>
        <button type="button" className='h-8 rounded-md mt-5 flex gap-1 justify-center items-center text-sm w-24 bg-red-700 text-white hover:bg-red-800'
          onClick={() => authProvider.logout()}>
          <LogOut className='w-4' />
          Logout
        </button>
      </div>
      <Modal open={open} setOpen={setOpen}>
        <div className='flex flex-col gap-8 p-1'>
          <div className='flex flex-col gap-1'>
            <h2 className='text-lg font-semibold'>Switch Parcel</h2>
            <p>Are you sure you want to switch the parcel: {parcelData?.parcel_name}?</p>
          </div>
          <div className='flex gap-1 items-center'>
            <Button variants='outline' onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={() => {
              switchParcel()
              setOpen(false)
            }}>Confirm</Button>
          </div>
        </div>
      </Modal>

      {/* Cleanup Report Modal */}
      <Modal open={cleanupOpen} setOpen={setCleanupOpen}>
        <div className='flex flex-col gap-4 p-1'>
          <div className='flex flex-col gap-2'>
            <h2 className='text-lg font-semibold flex items-center gap-2'>
              <Icon icon='mdi:database-check' width={24} className='text-blue-600'/>
              Cleanup Report
            </h2>
            {cleanupReport && cleanupReport.success ? (
              <div className='space-y-2'>
                <div className='p-3 bg-green-50 border border-green-200 rounded-lg'>
                  <p className='font-semibold text-green-800'>
                    Total duplicates removed: {cleanupReport.report.total}
                  </p>
                </div>
                {cleanupReport.report.total > 0 && (
                  <div className='space-y-1 text-sm'>
                    {cleanupReport.report.teams > 0 && <p>• Teams: {cleanupReport.report.teams}</p>}
                    {cleanupReport.report.workers > 0 && <p>• Workers: {cleanupReport.report.workers}</p>}
                    {cleanupReport.report.zones > 0 && <p>• Zones: {cleanupReport.report.zones}</p>}
                    {cleanupReport.report.taskTypes > 0 && <p>• Task Types: {cleanupReport.report.taskTypes}</p>}
                    {cleanupReport.report.locations > 0 && <p>• Locations: {cleanupReport.report.locations}</p>}
                    {cleanupReport.report.workerAssignments > 0 && <p>• Worker Assignments: {cleanupReport.report.workerAssignments}</p>}
                    {cleanupReport.report.workerOuts > 0 && <p>• Worker Outs: {cleanupReport.report.workerOuts}</p>}
                    {cleanupReport.report.workerAssignmentLogs > 0 && <p>• Assignment Logs: {cleanupReport.report.workerAssignmentLogs}</p>}
                    {cleanupReport.report.workerTimeLogs > 0 && <p>• Time Logs: {cleanupReport.report.workerTimeLogs}</p>}
                    {cleanupReport.report.todos > 0 && <p>• Todos: {cleanupReport.report.todos}</p>}
                  </div>
                )}
                {cleanupReport.report.total === 0 && (
                  <p className='text-gray-600'>No duplicate entries found. Your data is clean!</p>
                )}
              </div>
            ) : (
              <div className='p-3 bg-red-50 border border-red-200 rounded-lg'>
                <p className='font-semibold text-red-800'>
                  Error: {cleanupReport?.error || 'Unknown error'}
                </p>
              </div>
            )}
          </div>
          <div className='flex gap-2 items-center justify-end'>
            <Button onClick={() => setCleanupOpen(false)}>Close</Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
export default Settings
